/**
 * Real self-fetching entity-wide leave-balances hook (#wagepro-leave-phase2).
 * Fetches ALL employee leave balances for an entity in one call via
 * `payroll.listEntityLeaveBalances`. Used by the Balances tab on
 * `leave_screen.tsx` (the employer-level view).
 *
 * Mirrors `use_leave_balances.ts`'s DI-seam + state-machine pattern.
 *
 * ── Wire fields ─────────────────────────────────────────────────────────────
 *
 * The backend computes, per row (all ×100 scaled integers), for the leave
 * type's CURRENT leave year (`leave_year_start` … `leave_year_end`):
 *   - `balance_days_scaled`   — remaining balance after approved leave.
 *   - `taken_days_scaled`     — APPROVED request days that were debited
 *     from the balance and fall on or before the entity-local today.
 *   - `booked_days_scaled`    — the days of those same approvals that are
 *     still in the future (booked, not yet taken). Optional on the wire so a
 *     web build reaching an API from before the field (rolling deploy) reads 0.
 *   - `total_days_scaled`     — balance + taken + booked + paid out
 *     (encashed): the full entitlement available this leave year.
 *   - `pending_days_scaled`   — sum of PENDING request days.
 *   - `available_days_scaled` — balance − pending.
 *
 * ── Recalculate ─────────────────────────────────────────────────────────────
 *
 * `apply_entitlements` calls `payroll.applyLeaveEntitlements` (manager-only,
 * idempotent) and reloads. The listing itself already reconciles leave
 * policies, so this is an explicit "apply policies now" affordance, not a
 * required step.
 */
import * as React from 'react';
import {
  getTrpcClient,
  isForbiddenTrpcError,
  isPayrollAddonRequiredError,
} from '../lib/trpc.js';
import {
  useOAuthEntityContext,
  type OAuthEntityContext,
} from '../lib/auth/oauth_entity_context.js';

export interface EntityLeaveBalanceRow {
  id: string;
  employee_id: string;
  entity_id: string;
  leave_type: string;
  balance_days_scaled: number;
  total_days_scaled: number;
  taken_days_scaled: number;
  booked_days_scaled: number;
  pending_days_scaled: number;
  available_days_scaled: number;
  /** Current leave year for this type, `YYYY-MM-DD` (inclusive). */
  leave_year_start: string;
  leave_year_end: string;
  /** Derived: `balance_days_scaled / 100`. */
  balance_days: number;
  /** Derived: `total_days_scaled / 100`. */
  total_days: number;
  /** Derived: `taken_days_scaled / 100`. */
  taken_days: number;
  /** Derived: `booked_days_scaled / 100`. */
  booked_days: number;
  /** Derived: `pending_days_scaled / 100`. */
  pending_days: number;
  /** Derived: `available_days_scaled / 100`. */
  available_days: number;
}

export interface EntityLeaveBalancesFetchInput {
  firm_id: string;
  entity_id: string;
}

/** Wire shape from the backend — excludes derived `*_days` fields. */
export interface RawEntityLeaveBalanceRow {
  id: string;
  employee_id: string;
  entity_id: string;
  leave_type: string;
  balance_days_scaled: number;
  total_days_scaled: number;
  taken_days_scaled: number;
  /** Absent from an API that predates the field — read as 0. */
  booked_days_scaled?: number;
  pending_days_scaled: number;
  available_days_scaled: number;
  leave_year_start: string;
  leave_year_end: string;
}

export interface EntityLeaveBalancesFetchDeps {
  listEntityLeaveBalances: (
    input: EntityLeaveBalancesFetchInput,
  ) => Promise<{ balances: RawEntityLeaveBalanceRow[] }>;
}

function defaultEntityLeaveBalancesFetchDeps(): EntityLeaveBalancesFetchDeps {
  const client = getTrpcClient();
  return {
    listEntityLeaveBalances: (input) =>
      client.payroll.listEntityLeaveBalances.query(input),
  };
}

export type EntityLeaveBalancesLoadFailure = 'entitlement_required' | 'error';

function classifyLoadFailure(err: unknown): EntityLeaveBalancesLoadFailure {
  return isPayrollAddonRequiredError(err) ? 'entitlement_required' : 'error';
}

export type EntityLeaveBalancesFetchResult =
  | { outcome: 'success'; balances: EntityLeaveBalanceRow[] }
  | { outcome: 'failure'; failure: EntityLeaveBalancesLoadFailure };

export function adaptEntityLeaveBalanceRow(
  raw: RawEntityLeaveBalanceRow,
): EntityLeaveBalanceRow {
  const booked_days_scaled = raw.booked_days_scaled ?? 0;
  return {
    ...raw,
    booked_days_scaled,
    balance_days: raw.balance_days_scaled / 100,
    total_days: raw.total_days_scaled / 100,
    taken_days: raw.taken_days_scaled / 100,
    booked_days: booked_days_scaled / 100,
    pending_days: raw.pending_days_scaled / 100,
    available_days: raw.available_days_scaled / 100,
  };
}

export async function fetchEntityLeaveBalances(
  input: EntityLeaveBalancesFetchInput,
  deps?: EntityLeaveBalancesFetchDeps,
  isCancelled: () => boolean = () => false,
): Promise<EntityLeaveBalancesFetchResult> {
  try {
    const resolvedDeps = deps ?? defaultEntityLeaveBalancesFetchDeps();
    const { balances } = await resolvedDeps.listEntityLeaveBalances(input);
    return {
      outcome: 'success',
      balances: balances.map(adaptEntityLeaveBalanceRow),
    };
  } catch (err) {
    if (!isCancelled()) {
      console.error('[wagepro-web] Failed to load entity leave balances:', err);
    }
    return { outcome: 'failure', failure: classifyLoadFailure(err) };
  }
}

// ── Recalculate entitlements ────────────────────────────────────────────────

export type ApplyEntitlementsResult =
  | { outcome: 'success'; balances_changed: number }
  | { outcome: 'failure'; failure: 'forbidden' | 'error' };

export interface ApplyEntitlementsDeps {
  applyLeaveEntitlements: (
    input: EntityLeaveBalancesFetchInput,
  ) => Promise<{ grants_written: number; balances_changed: number }>;
}

function defaultApplyEntitlementsDeps(): ApplyEntitlementsDeps {
  const client = getTrpcClient();
  return {
    applyLeaveEntitlements: (input) =>
      client.payroll.applyLeaveEntitlements.mutate(input),
  };
}

/** Run the entitlement engine for every active employee of the entity. */
export async function applyLeaveEntitlements(
  input: EntityLeaveBalancesFetchInput,
  deps?: ApplyEntitlementsDeps,
): Promise<ApplyEntitlementsResult> {
  try {
    const resolvedDeps = deps ?? defaultApplyEntitlementsDeps();
    const result = await resolvedDeps.applyLeaveEntitlements(input);
    return { outcome: 'success', balances_changed: result.balances_changed };
  } catch (err) {
    console.error('[wagepro-web] leave: apply entitlements failed:', err);
    return {
      outcome: 'failure',
      failure: isForbiddenTrpcError(err) ? 'forbidden' : 'error',
    };
  }
}

export type EntityLeaveBalancesState =
  | { status: 'loading' }
  | { status: 'no_entity' }
  | { status: 'entitlement_required' }
  | { status: 'error' }
  | { status: 'ready'; balances: EntityLeaveBalanceRow[] };

export function resolveEntityLeaveBalancesState(input: {
  entityContext: OAuthEntityContext;
  balances: EntityLeaveBalanceRow[] | undefined;
  failure: EntityLeaveBalancesLoadFailure | undefined;
}): EntityLeaveBalancesState {
  if (input.entityContext.status === 'no_entity') {
    return { status: 'no_entity' };
  }
  if (input.balances !== undefined) {
    return { status: 'ready', balances: input.balances };
  }
  if (input.failure === 'entitlement_required') {
    return { status: 'entitlement_required' };
  }
  if (input.failure === 'error') {
    return { status: 'error' };
  }
  return { status: 'loading' };
}

export interface UseEntityLeaveBalancesResult {
  state: EntityLeaveBalancesState;
  balances: EntityLeaveBalanceRow[];
  is_loading: boolean;
  retry: () => void;
  /**
   * Apply leave policies now and reload. `undefined` until the entity
   * context is ready. The caller gates visibility on a write-capable grant.
   */
  apply_entitlements?: () => Promise<ApplyEntitlementsResult>;
}

/**
 * @param refresh_key - any value that changes whenever leave requests were
 *   mutated elsewhere (see `useLeaveRequests().mutation_version`). The
 *   pending/available columns are derived from the request queue, so a
 *   change here re-fetches the balances instead of showing stale totals.
 *   A refresh for the SAME entity keeps the current rows on screen until
 *   the new ones arrive (no skeleton flash); switching entity, or a failed
 *   fetch, clears them.
 * @param deps - injectable fetch seam (tests); defaults to the real client.
 */
export function useEntityLeaveBalances(
  refresh_key = 0,
  deps?: EntityLeaveBalancesFetchDeps,
): UseEntityLeaveBalancesResult {
  const entityContext = useOAuthEntityContext();
  const [balances, setBalances] = React.useState<
    EntityLeaveBalanceRow[] | undefined
  >(undefined);
  const [failure, setFailure] = React.useState<
    EntityLeaveBalancesLoadFailure | undefined
  >(undefined);
  const [reloadToken, setReloadToken] = React.useState(0);
  // Held in a ref so a caller passing a fresh `deps` object each render does
  // not re-trigger the fetch effect.
  const depsRef = React.useRef(deps);
  React.useLayoutEffect(() => {
    depsRef.current = deps;
  }, [deps]);
  const loadedEntityKey = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (entityContext.status !== 'ready') return;
    const firmId: string = entityContext.firm_id;
    const entityId: string = entityContext.entity_id;
    const entityKey = `${firmId}:${entityId}`;
    let cancelled = false;
    if (loadedEntityKey.current !== entityKey) {
      setBalances(undefined);
    }
    setFailure(undefined);

    async function load(): Promise<void> {
      const result = await fetchEntityLeaveBalances(
        { firm_id: firmId, entity_id: entityId },
        depsRef.current,
        () => cancelled,
      );
      if (cancelled) return;
      if (result.outcome === 'success') {
        loadedEntityKey.current = entityKey;
        setBalances(result.balances);
      } else {
        loadedEntityKey.current = null;
        setBalances(undefined);
        setFailure(result.failure);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [entityContext, reloadToken, refresh_key]);

  const state = React.useMemo(
    () => resolveEntityLeaveBalancesState({ entityContext, balances, failure }),
    [entityContext, balances, failure],
  );

  const retry = React.useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  const apply_entitlements =
    entityContext.status === 'ready'
      ? async (): Promise<ApplyEntitlementsResult> => {
          const result = await applyLeaveEntitlements({
            firm_id: entityContext.firm_id,
            entity_id: entityContext.entity_id,
          });
          if (result.outcome === 'success') retry();
          return result;
        }
      : undefined;

  return {
    state,
    balances: state.status === 'ready' ? state.balances : [],
    is_loading: state.status === 'loading',
    retry,
    apply_entitlements,
  };
}
